import { Component } from 'react'

import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'

import { formatRetailerWithLocation } from 'formatters/orders'
import { formatDropdownOptions } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import Dropdown from 'components/Core/Dropdown/Dropdown'
import Modal from 'components/Core/Modal'
import { ORDERS_CART } from 'routes/paths'

import messages from './DuplicateOrdersModal.messages'
import styles from './DuplicateOrdersModal.module.css'
import { MINIMUM_CHARACTERS_QUERY } from './DuplicateOrdersModal.queries'
import { duplicateOrders } from './DuplicateOrdersModal.request'

const DEBOUNCE_TIME = 500

export default class DuplicateOrdersModal extends Component {
  state = {
    duplicatingOrders: false,
    retailerOptions: [],
  }

  componentDidUpdate(prevProps) {
    const finishedLoading =
      prevProps.connectedAccountsQueryLoading &&
      !this.props.connectedAccountsQueryLoading
    const searchTextChanged =
      prevProps.retailerSearchText !== this.props.retailerSearchText
    const minimumCharactersReached =
      this.props.retailerSearchText.length < MINIMUM_CHARACTERS_QUERY

    if (finishedLoading || searchTextChanged) {
      /* eslint-disable react/no-did-update-set-state */
      this.setState({
        retailerOptions: minimumCharactersReached
          ? []
          : this.getRetailerOptions(),
      })
    }
  }

  componentWillUnmount() {
    this.props.changeRetailerSearchText('')
  }

  onRetailerDropdownChange = (_, { value }) => {
    if (value !== this.props.selectedRetailerId) {
      this.props.setSelectedRetailerId(value)
    }
  }

  getRetailerOptions = () =>
    formatDropdownOptions(
      this.props.retailers,
      formatRetailerWithLocation,
      true,
      'notranslate',
    )

  handleDuplicateOrders = () => {
    this.setState({
      duplicatingOrders: true,
    })

    const { selectedOrders, retailers, selectedRetailerId } = this.props

    const selectedRetailer = retailers.find(
      (retailer) => retailer.id === selectedRetailerId,
    )

    const selectedOrdersIds = selectedOrders.map(
      (selectedOrder) => selectedOrder.webOrderLookupId,
    )

    const data = {
      orderIds: selectedOrdersIds,
      retailer: {
        id: fromGlobalId(selectedRetailerId).id,
        displayName: selectedRetailer.displayName,
      },
    }

    return duplicateOrders(data).then((result) => {
      this.setState({
        duplicatingOrders: false,
      })
      this.props.handleResetOrder()
      this.props.onClose()
      this.props.renderActionsToasterMessage?.()
      if (this.props.redirectOnSuccess) {
        this.props.navigate(ORDERS_CART)
      }
      if (this.props.shouldUpdateCart) {
        this.props.setShouldUpdateCart(true)
      }
      return result.data
    })
  }

  handleSearchRetailer = debounce((e, { searchQuery }) => {
    if (searchQuery !== this.props.retailerSearchText) {
      this.props.changeRetailerSearchText(searchQuery)
    }
  }, DEBOUNCE_TIME)

  render() {
    const { duplicatingOrders, retailerOptions } = this.state

    const {
      loadingConnectedRetailers,
      connectedAccountsQueryLoading,
      selectedRetailerId,
    } = this.props

    return (
      <Modal
        primaryActionClose={false}
        loading={loadingConnectedRetailers || duplicatingOrders}
        open
        onClose={this.props.onClose}
        primaryActionLabel={messages.duplicate}
        title={messages.duplicateOrders}
        primaryDisabled={!selectedRetailerId}
        primaryActionOnClick={this.handleDuplicateOrders}
      >
        <p className={styles.label}>{messages.selectRetailerToDuplicate}</p>
        <Dropdown
          onChange={this.onRetailerDropdownChange}
          onSearchChange={this.handleSearchRetailer}
          loading={connectedAccountsQueryLoading}
          options={retailerOptions}
          placeholder={messages.startTyping}
          search
          value={selectedRetailerId}
          className={selectedRetailerId ? 'notranslate' : ''}
        />
      </Modal>
    )
  }
}

DuplicateOrdersModal.propTypes = {
  onClose: PropTypes.func,
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      locations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
        }),
      ),
    }),
  ),
  selectedOrders: PropTypes.arrayOf(PropTypes.object),
  loadingConnectedRetailers: PropTypes.bool,
  changeRetailerSearchText: PropTypes.func.isRequired,
  retailerSearchText: PropTypes.string.isRequired,
  selectedRetailerId: PropTypes.string,
  setSelectedRetailerId: PropTypes.func.isRequired,
  handleResetOrder: PropTypes.func.isRequired,
  connectedAccountsQueryLoading: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  renderActionsToasterMessage: PropTypes.func,
  redirectOnSuccess: PropTypes.bool,
  shouldUpdateCart: PropTypes.bool,
}

DuplicateOrdersModal.defaultProps = {
  onClose: () => {},
  loadingConnectedRetailers: false,
  selectedOrders: [],
  retailers: [],
  connectedAccountsQueryLoading: false,
  selectedRetailerId: null,
  redirectOnSuccess: true,
  shouldUpdateCart: false,
}
